import RumClient from './RumClient';
import { v4 as uuid } from 'uuid';
import { DuplicationCheckStatus } from '@amzn/awscat-pattern-library-service-model';

/**
 * Telemetry helpers for the Automated Pre-authoring wizard.
 *
 * Every public function in this module records a single CloudWatch RUM custom
 * event under the {@link AUTOMATED_PREAUTHORING_EVENT_ID} event type.  Events
 * are enriched with a per-wizard-session ID (see {@link initSession}) and an
 * ISO-8601 timestamp before being forwarded to {@link RumClient.recordEvent}.
 *
 * Three event sub-types are supported:
 *
 * | Sub-type                        | Fired when                                                |
 * |---------------------------------|-----------------------------------------------------------|
 * | `WizardSessionStarted`          | The wizard mounts and a new session begins.               |
 * | `DuplicationScanActionTaken`    | The user acts on the duplicate-check results (Step 4).    |
 * | `WizardStepNavigation`          | The user navigates between wizard steps.                  |
 * | `DuplicationScanComplete`       | The duplication scan finishes (success, partial, or fail).|
 *
 * @module PreapprovalTelemetry
 */

/**
 * Actions a user can take on the duplication scan step (Step 4).
 *
 * - `CANCEL`   – The user cancels the wizard from the scan results screen.
 * - `PREVIOUS` – The user navigates back to a previous step.
 * - `CREATE`   – The user confirms uniqueness and proceeds to create the content.
 */
export type DuplicationScanAction = 'CANCEL' | 'PREVIOUS' | 'CREATE';

/**
 * Navigation actions available across all wizard steps.
 *
 * - `NEXT`     – Advance to the next step.
 * - `PREVIOUS` – Return to the previous step.
 * - `CANCEL`   – Abandon the wizard entirely.
 * - `SUBMIT`   – Submit the wizard (final step only).
 */
export type WizardStepAction = 'NEXT' | 'PREVIOUS' | 'CANCEL' | 'SUBMIT';

/** CloudWatch RUM event type shared by all pre-authoring telemetry events. */
export const AUTOMATED_PREAUTHORING_EVENT_ID = 'automated_preauthoring';

/** UUID that groups all events emitted during a single wizard session. */
let currentSessionId: string | undefined;

/** The authenticated user's identifier (e.g. `AmazonCorporate_alias`), set once per session. */
let currentUserId: string | undefined;

/** Discriminated union of every event this module can emit. */
type PreauthoringEvent =
  | WizardSessionStartedEvent
  | DuplicationScanActionTakenEvent
  | WizardStepNavigationEvent
  | DuplicationScanCompleteEvent
  | DuplicateResultClickthroughEvent;

/** Recorded when the wizard mounts and a new session begins. */
interface WizardSessionStartedEvent {
  /** Discriminator for the event union. */
  readonly subType: 'WizardSessionStarted';
}

/** Recorded when the user takes an action on the duplicate-check results. */
interface DuplicationScanActionTakenEvent {
  /** Discriminator for the event union. */
  readonly subType: 'DuplicationScanActionTaken';
  /** Server-assigned content identifier. */
  readonly contentId: string;
  /** Workflow instance number. */
  readonly workflowId: number;
  /** The action the user chose. */
  readonly action: DuplicationScanAction;
  /** Milliseconds the user spent reviewing results before acting. */
  readonly decisionTimeMs: number;
}

/** Recorded each time the user moves between wizard steps. */
interface WizardStepNavigationEvent {
  /** Discriminator for the event union. */
  readonly subType: 'WizardStepNavigation';
  /** Zero-based index of the step the user is leaving. */
  readonly sourceStepIndex: number;
  /** Zero-based index of the step the user is entering. */
  readonly destinationStepIndex: number;
  /** The navigation action that triggered the transition. */
  readonly action: WizardStepAction;
  /** Milliseconds the user spent on the source step. */
  readonly durationMs: number;
}

/** Recorded when the duplication scan finishes (regardless of outcome). */
interface DuplicationScanCompleteEvent {
  /** Discriminator for the event union. */
  readonly subType: 'DuplicationScanComplete';
  /** Whether the scan succeeded, partially succeeded, or failed. */
  readonly scanStatus: DuplicationCheckStatus;
  /** Server-assigned content identifier. */
  readonly contentId: string;
  /** Workflow instance number. */
  readonly workflowId: number;
  /** Correlation id from the scan — join key to agent logs. */
  readonly traceId: string;
  /** Wall-clock milliseconds from scan start to completion. */
  readonly durationMs: number;
  /** Whether the server flagged the content as harmful. */
  readonly isContentHarmful: boolean;
  /** Whether the server flagged the content as invalid. */
  readonly isContentInvalid: boolean;
  /** Optional detail when scanStatus is FAILURE or PARTIAL_SUCCESS. */
  readonly failureReason?: string;
}

/**
 * Enriches the payload with the current session ID, user ID, and timestamp,
 * then forwards it to CloudWatch RUM.
 */
function emit(payload: PreauthoringEvent): void {
  RumClient.recordEvent(AUTOMATED_PREAUTHORING_EVENT_ID, {
    ...payload,
    sessionId: currentSessionId,
    userId: currentUserId,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Starts a new telemetry session for the wizard.
 * Call once when the `CreateContentWizard` mounts so that every subsequent
 * event can be correlated back to the same user journey.
 *
 * @param userId - The authenticated user's identifier (e.g. `AmazonCorporate_alias`).
 */
export function initSession(userId: string): void {
  currentSessionId = uuid();
  currentUserId = userId;
  emit({ subType: 'WizardSessionStarted' });
}

/**
 * Records the action a user takes after reviewing duplicate-check results.
 *
 * @param params.contentId      - Server-assigned content identifier.
 * @param params.action         - The action chosen by the user.
 * @param params.decisionTimeMs - Time in ms the user spent before acting.
 */
export function recordDuplicationScanAction(
  params: Omit<DuplicationScanActionTakenEvent, 'subType'>,
): void {
  emit({ subType: 'DuplicationScanActionTaken', ...params });
}

/**
 * Records a wizard step navigation event.
 *
 * @param params.sourceStepIndex      - Index of the step being left.
 * @param params.destinationStepIndex - Index of the step being entered.
 * @param params.action               - The navigation action (NEXT, PREVIOUS, etc.).
 * @param params.durationMs           - Time in ms spent on the source step.
 */
export function recordWizardStepNavigation(
  params: Omit<WizardStepNavigationEvent, 'subType'>,
): void {
  emit({ subType: 'WizardStepNavigation', ...params });
}

/**
 * Records the completion of a duplication scan.
 *
 * @param params.contentId     - Server-assigned content identifier.
 * @param params.scanStatus    - Outcome of the scan (from DuplicationCheckStatus).
 * @param params.durationMs    - Wall-clock time in ms from scan start to finish.
 * @param params.failureReason - Optional detail when the scan did not fully succeed.
 */
export function recordDuplicationScanComplete(
  params: Omit<DuplicationScanCompleteEvent, 'subType'>,
): void {
  emit({ subType: 'DuplicationScanComplete', ...params });
}

/** Recorded when the user clicks through to view a flagged duplicate article. */
interface DuplicateResultClickthroughEvent {
  /** Discriminator for the event union. */
  readonly subType: 'DuplicateResultClickthrough';
  /** Server-assigned content identifier. */
  readonly contentId: string;
  /** Workflow instance number. */
  readonly workflowId: number;
  /** Correlation id from the scan — join key to the agent's SCAN_RESULTS log. */
  readonly traceId: string;
  /** The id of the article the user clicked. */
  readonly articleId: string;
}

/**
 * Records a user clicking through to view a duplicate result article.
 *
 * @param params.traceId   - Correlation id from the duplication scan response.
 * @param params.articleId - Id of the article the user clicked.
 */
export function recordDuplicateResultClickthrough(
  params: Omit<DuplicateResultClickthroughEvent, 'subType'>,
): void {
  emit({ subType: 'DuplicateResultClickthrough', ...params });

  // The click follows an external link, so flush now via beacon — the periodic
  // dispatch may not fire before the tab navigates or loses focus.
  RumClient.flushBeacon();
}
